import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import { getSurveyIcon, toast } from '@utilities';
import {
  createModalSurvey,
  readInstantSurveyType,
  validateInstantSurveyPrompts,
} from '@api/instant_survey';
import type { ModalPromptRequirement, Prompt } from '@api/instant_survey/types';
import { useAppSelector } from '@redux/hooks';

import { generateTemporarySurveyTitle } from '../../../CreateInstantSurvey/utilities/helpers';

import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../../CreateInstantSurvey/components/Panelists/utilities/helpers';

import PreBuildModal from '../../../../components/PreBuildModal';
import type { LegacySurveyRoute } from '../../utilities/helpers';

import {
  hasValidLightHeavyRange,
  hasValidPercentPurchaseVolume,
  isPercentPurchaseVolumeAnswer,
  transformPromptAnswers,
} from './utilities/helpers';
import type { PromptAnswer } from './utilities/helpers';

interface ModalBuildSurveyProps {
  isModalOpen: boolean;
  route: LegacySurveyRoute;
  setIsModalOpen: (isOpen: boolean) => void;
}

const ModalBuildSurvey = ({ isModalOpen, route, setIsModalOpen }: ModalBuildSurveyProps) => {
  const [activePrompt, setActivePrompt] = useState<null | Prompt>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalPromptRequirements, setModalPromptRequirements] = useState<ModalPromptRequirement>(
    {}
  );
  const [modalPrompts, setModalPrompts] = useState<Prompt[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, unknown>>({});
  const [surveyTitle, setSurveyTitle] = useState('');
  const [transformedAnswersMap, setTransformedAnswersMap] = useState<
    Record<string, PromptAnswer[]>
  >({});

  const navigate = useNavigate();
  const panel = useAppSelector((state) => state.user.data.panel);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isModalOpen) {
      setSurveyTitle(route?.label || '');
    } else {
      setSurveyTitle('');
      setSelectedAnswers({});
      setTransformedAnswersMap({});
      setActivePrompt(null);
      setModalPrompts([]);
    }
  }, [isModalOpen, route?.label]);

  useEffect(() => {
    if (!isModalOpen) return;

    const fetchPrompts = async () => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      try {
        const data = await readInstantSurveyType({
          id: route.path,
          signal: controller.signal,
        });
        if (controller.signal.aborted || abortControllerRef.current !== controller) return;

        const prompts = data?.prompts || [];
        const modalPrompts = prompts.filter((prompt) => prompt.isModalPrompt);
        setModalPromptRequirements(data.modalPromptRequirements[0] ?? {});
        setModalPrompts(modalPrompts);
        setActivePrompt(modalPrompts[0] || null);
      } catch (error) {
        if (controller.signal.aborted) return;

        console.error('Error fetching survey prompts:', error);
        toast.error('Failed to load prompts.');
      } finally {
        if (abortControllerRef.current === controller && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchPrompts();

    return () => abortControllerRef.current?.abort();
  }, [isModalOpen, route?.path]);

  const saveSurveyDraft = useCallback(
    async (
      promptAnswers: ReturnType<typeof transformPromptAnswers>
    ): Promise<null | { id: number }> => {
      try {
        return await createModalSurvey({
          country: panel?.abbrev?.toLowerCase() || '',
          instantSurveyType: route?.path,
          promptAnswers,
          title: surveyTitle.trim() || generateTemporarySurveyTitle(),
        });
      } catch (error) {
        console.error('Save error:', error);
        toast.error('Failed to save your survey draft.');
        return null;
      }
    },
    [route?.path, surveyTitle]
  );

  const validateSurveyById = useCallback(async (id: number) => {
    try {
      await validateInstantSurveyPrompts({ id });
      return true;
    } catch (error) {
      console.error('Validation error:', error);
      return false;
    }
  }, []);
  const buildSurveyDraft = useCallback(async () => {
    try {
      const promptAnswers = transformPromptAnswers(Object.values(transformedAnswersMap).flat());

      const survey = await saveSurveyDraft(promptAnswers);
      if (!survey) return null;

      const isValid = await validateSurveyById(survey.id);
      return isValid ? survey : null;
    } catch (error) {
      console.error('Build error:', error);
      toast.error('An error occurred while preparing your survey.');
      return null;
    }
  }, [transformedAnswersMap, saveSurveyDraft, validateSurveyById]);

  const handleBuildSurvey = useCallback(async () => {
    setIsBuilding(true);
    const survey = await buildSurveyDraft();
    if (!survey) {
      toast.error('There was an error building your survey.');
      setIsBuilding(false);
      return;
    }

    navigate(
      `/dashboard/create-survey/${route?.path}?ref=${survey.id}&modal=true&autoValidate=true`
    );
    setIsModalOpen(false);
  }, [buildSurveyDraft, navigate, route?.path, setIsModalOpen]);

  const isNonEmpty = (value: unknown) =>
    Array.isArray(value) ? value.length > 0 : !isEmpty(value);
  const requiredFields = modalPrompts.filter((prompt) => prompt?.isRequiredForModal);
  const requiredPromptKeys = modalPromptRequirements.prompts || [];
  const hasRequiredPrompts =
    requiredPromptKeys.length > 0
      ? requiredPromptKeys.some((promptKey) => isNonEmpty(transformedAnswersMap?.[promptKey]))
      : true;

  const hasAllRequiredFields = requiredFields?.every(({ promptKey }) =>
    isNonEmpty(transformedAnswersMap?.[promptKey])
  );

  const hasValidPurchaseVolume = useMemo(() => {
    const purchaseVolumeAnswers = transformedAnswersMap['purchase_volume'];
    if (!purchaseVolumeAnswers) return true;

    return purchaseVolumeAnswers.every((answer) => {
      if (!answer?.values?.length) return false;

      return isPercentPurchaseVolumeAnswer(answer)
        ? hasValidPercentPurchaseVolume(answer.values as Array<number | string>)
        : hasValidLightHeavyRange(answer.values as Array<number | string>);
    });
  }, [transformedAnswersMap?.purchase_volume]);

  const hasValidPurchaseShift = useMemo(() => {
    const purchaseShiftAnswers = transformedAnswersMap['purchase_shift'];
    if (!purchaseShiftAnswers) return true;

    return purchaseShiftAnswers.every((answer) => {
      if (!answer?.values?.length) return false;
      return hasValidLightHeavyRange(answer.values as Array<number | string>);
    });
  }, [transformedAnswersMap?.purchase_shift]);

  const isBuildDisabled = !(
    hasRequiredPrompts &&
    hasAllRequiredFields &&
    hasValidPurchaseVolume &&
    hasValidPurchaseShift
  );

  return (
    /* @ts-ignore PreBuildModal is not typed yet */
    <PreBuildModal
      activePrompt={activePrompt}
      getPillTooltip={getPromptPillTooltip}
      getPillValue={getPromptPillValue}
      handleBuild={handleBuildSurvey}
      headerIcon={getSurveyIcon(route?.path)}
      isBuildDisabled={isBuildDisabled}
      isBuilding={isBuilding}
      isLoading={isLoading}
      isModalOpen={isModalOpen}
      name={surveyTitle}
      prompts={modalPrompts}
      routeLabel={route?.label}
      searchPrompts={route?.searchPrompts}
      selectedAnswers={selectedAnswers}
      setActivePrompt={setActivePrompt}
      setIsModalOpen={setIsModalOpen}
      setName={setSurveyTitle}
      setSelectedAnswers={setSelectedAnswers}
      setTransformedAnswersMap={setTransformedAnswersMap}
      transformedAnswersMap={transformedAnswersMap}
      transformPromptSelections={transformPromptSelections}
      type="survey"
    />
  );
};

export default ModalBuildSurvey;
